import React from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { isoDateToUPCCovertor } from "./SiteAboutPageUtils/isoDateToUPCCovertor";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Switch,
  TableHead,
  Autocomplete,
  TextField,
} from "@mui/material";
import FormGroup from "@mui/material/FormGroup";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import EditIcon from "@mui/icons-material/Edit";
import AutoPatrolScheduleDropDown from "./AutoPatrolScheduleDropDown";
import {
  AutoPatrolFlexIgnoreZonesOnboardingDialog,
  AutoPatrolFlexIgnoreZonesOffboardingDialog,
  AutoPatrolFlexIgnoreZonesScheduleSection,
} from "./AutoPatrolFlexIgnoreZones";
import { ProductBulkUpdateDialog } from "./ProductBulkUpdateDialog";

type SchedulesTabAutopatrolProps = {
  editMode: boolean;
  permissions?: { add_cameras?: boolean; edit_cameras?: boolean };
  onEditSchedulesClick?: () => void;
  onSaveSchedulesClick?: () => void;
  onCancelSchedulesClick?: () => void;
};

const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const lowerCaseWords = (str: string) => {
  return str.replace(/\b\w/g, (char) => char.toLowerCase());
};

export const SchedulesTabAutopatrol = ({
  editMode,
  permissions,
  onEditSchedulesClick,
  onSaveSchedulesClick,
  onCancelSchedulesClick,
}: SchedulesTabAutopatrolProps) => {
  const { flags } = useFeatureFlag();
  const showProductsCheckboxesApSchedulesTab =
    flags.showProductsCheckboxesApSchedulesTab;
  const showAutoPatrolFlexIgnoreZones =
    flags.showAutoPatrolFlexIgnoreZones === true;
  const showApProductBulkUpdate = flags.showApProductBulkUpdate === true;
  const navigate = useNavigate();
  const {
    autoPatrolSchedules,
    selectedAutoPatrolScheduleIndex,
    healthCheckDataZustand,
    healthCheckMetricsData,
    autoPatrolScheduleHealthchckMetrics,
    setAutoPatrolSchedules,
    setAutoPatrolScheduleHealthchckMetrics,
    setAutoPatrolSchedulesUpdatedBoolean,
    updatedAutoPatrolScheduleIndexes,
    setUpdatedAutoPatrolScheduleIndexes,
    setAutoPatrolScheduleHealthchckMetricsUpdatedBoolean,
    updatedAutoPatrolScheduleHealthchckMetricsIds,
    setUpdatedAutoPatrolScheduleHealthchckMetricsIds,
    autoPatrolCameraProducts,
    setAutoPatrolCameraProducts,
    updatedProductMetricsIds,
    setUpdatedProductMetricsIds,
    setProductMetricsWereUpdated,
    getAutoPatrolScheduleHealthchckMetrics,
    vCHOnlySchedules,
    autoPatrolFlexIgnoreZonesEnabledBySite,
    setAutoPatrolFlexIgnoreZonesEnabledForSite,
    autoPatrolFlexIgnoreZonesWizardOpenBySite,
    setAutoPatrolFlexIgnoreZonesWizardOpenForSite,
    resetAutoPatrolFlexIgnoreZonesForSite,
  } = useSiteAboutStore();
  const [offboardingDialogOpen, setOffboardingDialogOpen] = React.useState(false);
  const [productBulkUpdateDialogOpen, setProductBulkUpdateDialogOpen] =
    React.useState(false);

  const titlesArray = ["Status", "Patrol Type", "Cadence", "Updated Date"];

  const sensitivityOptions = ["low", "medium", "high"];

  // Add safety check for selectedAutoPatrolScheduleIndex
  const currentSchedule = autoPatrolSchedules[selectedAutoPatrolScheduleIndex];
  const scheduleId = currentSchedule?.id;
  const siteId = Number(
    currentSchedule?.customer?.id ||
      JSON.parse(localStorage.getItem("siteInfo") || "{}")?.id ||
      0
  );
  const autoPatrolOnlySchedules = React.useMemo(
    () =>
      autoPatrolSchedules.filter(
        (schedule) => schedule?.patrolType === "AutoPatrol"
      ),
    [autoPatrolSchedules]
  );
  const flexIgnoreZonesEnabled =
    autoPatrolFlexIgnoreZonesEnabledBySite[siteId] === true;
  const flexWizardOpen = autoPatrolFlexIgnoreZonesWizardOpenBySite[siteId] === true;
  const isAutoPatrolSchedule = currentSchedule?.patrolType === "AutoPatrol";
  const scheduleMetrics = autoPatrolScheduleHealthchckMetrics[scheduleId] || [];

  const getMetricByType = (metricType: string) =>
    scheduleMetrics.find((metric: any) => metric?.metric_type?.value === metricType);

  const sceneChangeMetric = getMetricByType("scene_change_check");
  const imageQualityMetric = getMetricByType("image_quality_check");

  const vchMetricTypes = [
    "connectivity_check",
    "scene_change_check",
    "motion_status_check",
    "recording_check",
    "stream_quality_check",
    "image_quality_check",
  ];

  const metricLabelMap: Record<string, string> = {
    connectivity_check: "Connectivity",
    scene_change_check: "Scene Change",
    motion_status_check: "Motion Status",
    recording_check: "Recording",
    stream_quality_check: "Stream Quality",
    image_quality_check: "Image Quality",
  };

  const vchMetrics = vchMetricTypes
    .map((metricType) => getMetricByType(metricType))
    .filter(Boolean);

  // Get products for the current schedule
  const scheduleProducts = autoPatrolCameraProducts[scheduleId] || [];

  const markScheduleHealthMetricsUpdated = () => {
    setAutoPatrolScheduleHealthchckMetricsUpdatedBoolean(true);
    const nextUpdatedIds = updatedAutoPatrolScheduleHealthchckMetricsIds.includes(
      scheduleId
    )
      ? updatedAutoPatrolScheduleHealthchckMetricsIds
      : [...updatedAutoPatrolScheduleHealthchckMetricsIds, scheduleId];
    setUpdatedAutoPatrolScheduleHealthchckMetricsIds(nextUpdatedIds);
  };

  const updateMetricEnabled = (metricType: string, enabled: boolean) => {
    const updatedScheduleMetrics = scheduleMetrics.map((metric: any) => {
      if (metric?.metric_type?.value === metricType) {
        return { ...metric, enabled };
      }
      return metric;
    });

    setAutoPatrolScheduleHealthchckMetrics({
      ...autoPatrolScheduleHealthchckMetrics,
      [scheduleId]: updatedScheduleMetrics,
    });
    markScheduleHealthMetricsUpdated();
  };

  // Helper to find if a product is enabled by its display_name
  const isProductEnabled = (displayName: string): boolean => {
    const product = scheduleProducts.find(
      (p) => p.metric_label.display_name === displayName
    );
    return product?.enabled || false;
  };

  type ProductGroup = {
    parentName: string;
    color: string;
    children: string[];
  };

  const areAllChildrenEnabled = (group: ProductGroup): boolean =>
    group.children.length > 0 &&
    group.children.every((displayName) => isProductEnabled(displayName));

  const isSomeButNotAllChildrenEnabled = (group: ProductGroup): boolean => {
    const enabledCount = group.children.filter((displayName) =>
      isProductEnabled(displayName)
    ).length;
    return enabledCount > 0 && enabledCount < group.children.length;
  };

  const handleMasterCheckboxChange = (group: ProductGroup) => {
    if (!scheduleId) return;
    const allEnabled = areAllChildrenEnabled(group);
    const newEnabled = !allEnabled;

    const childDisplayNames = new Set(group.children);
    const updatedProducts = scheduleProducts.map((p) => {
      if (childDisplayNames.has(p.metric_label.display_name)) {
        return { ...p, enabled: newEnabled };
      }
      return p;
    });

    setAutoPatrolCameraProducts({
      ...autoPatrolCameraProducts,
      [scheduleId]: updatedProducts,
    });

    const currentIds = updatedProductMetricsIds[scheduleId] || [];
    let newIds = [...currentIds];

    for (const displayName of group.children) {
      const product = scheduleProducts.find(
        (p) => p.metric_label.display_name === displayName
      );
      if (!product) continue;
      const wasEnabled = product.enabled;
      if (wasEnabled === newEnabled) continue;
      if (newIds.includes(product.id)) {
        newIds = newIds.filter((id) => id !== product.id);
      } else {
        newIds = [...newIds, product.id];
      }
    }

    setUpdatedProductMetricsIds({
      ...updatedProductMetricsIds,
      [scheduleId]: newIds,
    });
    setProductMetricsWereUpdated(true);
  };

  // Product groups with parent names, children, and colors
  const productGroups = [
    {
      parentName: "Intruder",
      color: "#FF8400", // ActuateOrange
      children: [
        "Intruder",
        "Vehicle (grouped car, truck, bus)",
        "Bike (grouped bicycle, motorcycle)",
      ],
    },
    {
      parentName: "Crowd",
      color: "#2196F3", // ActuateBlue
      children: ["Crowd"],
    },
    {
      parentName: "Fire",
      color: "#F44336", // Red
      children: ["Fire", "Smoke"],
    },
    {
      parentName: "Non-UPS Vehicle ID",
      color: "#03A9F4", // Lighter blue
      children: ["Non-UPS"],
    },
    {
      parentName: "Vehicle ID",
      color: "#4CAF50", // Green
      children: [
        "Amazon",
        "DHL",
        "Fedex",
        "School Bus",
        "UPS",
        "USPS",
        "Fire Truck",
      ],
    },
  ];

  // Shorter labels for display
  const getShortLabel = (displayName: string): string => {
    const labelMap: { [key: string]: string } = {
      "Vehicle (grouped car, truck, bus)": "Vehicle",
      "Bike (grouped bicycle, motorcycle)": "Bike",
    };
    return labelMap[displayName] || displayName;
  };

  // Handler for product checkbox changes in edit mode
  const handleProductCheckboxChange = (displayName: string) => {
    const product = scheduleProducts.find(
      (p) => p.metric_label.display_name === displayName
    );

    if (!product || !scheduleId) return;

    // 1. Update autoPatrolCameraProducts - toggle the enabled value
    const updatedProducts = scheduleProducts.map((p) =>
      p.id === product.id ? { ...p, enabled: !p.enabled } : p
    );
    setAutoPatrolCameraProducts({
      ...autoPatrolCameraProducts,
      [scheduleId]: updatedProducts,
    });

    // 2. Update updatedProductMetricsIds - track which metrics were changed
    const currentIds = updatedProductMetricsIds[scheduleId] || [];
    let newIds: number[];

    if (currentIds.includes(product.id)) {
      // If already in the list, remove it (toggle back to original state)
      newIds = currentIds.filter((id) => id !== product.id);
    } else {
      // Add to the list
      newIds = [...currentIds, product.id];
    }

    setUpdatedProductMetricsIds({
      ...updatedProductMetricsIds,
      [scheduleId]: newIds,
    });

    // 3. Set productMetricsWereUpdated to true
    setProductMetricsWereUpdated(true);
  };

  const titlesArray2 = imageQualityMetric?.enabled
    ? sceneChangeMetric?.display
      ? isAutoPatrolSchedule
        ? ["Sensitivity", "Min Image Height", "Linked Number of Cameras"]
        : [
            "Sensitivity",
            "Health Metrics",
            "Min Image Height",
            "Linked Number of Cameras",
          ]
      : isAutoPatrolSchedule
      ? ["Min Image Height", "Linked Number of Cameras"]
      : ["Health Metrics", "Min Image Height", "Linked Number of Cameras"]
    : sceneChangeMetric?.display
    ? isAutoPatrolSchedule
      ? ["Sensitivity", "Linked Number of Cameras"]
      : ["Sensitivity", "Health Metrics", "Linked Number of Cameras"]
    : isAutoPatrolSchedule
    ? ["Linked Number of Cameras"]
    : ["Health Metrics", "Linked Number of Cameras"];

  React.useEffect(() => {
    localStorage.setItem(
      "backupHealthCheckMetricsData",
      JSON.stringify(healthCheckMetricsData)
    );
  }, [healthCheckMetricsData]);

  // Ensure health check metrics are loaded for the current schedule when missing.
  // Runs on mount/tab view and when entering edit mode (in case getAutoPatrolSchedules
  // didn't fire or the per-schedule API hadn't completed).
  React.useEffect(() => {
    if (
      scheduleId != null &&
      autoPatrolScheduleHealthchckMetrics[scheduleId] == null
    ) {
      getAutoPatrolScheduleHealthchckMetrics(
        navigate,
        String(scheduleId),
        true
      );
    }
  }, [
    scheduleId,
    autoPatrolScheduleHealthchckMetrics,
    getAutoPatrolScheduleHealthchckMetrics,
    navigate,
  ]);

  const schedulesHeader = (
    <div className="relative flex w-full items-center justify-between">
      <div className="flex items-center gap-4">
        <p className="font-black font-mulish text-[20px] pt-3 indent-4">
          Schedules
        </p>
        {showAutoPatrolFlexIgnoreZones && !vCHOnlySchedules && siteId > 0 && (
          <div className="pt-3 pl-3">
            <FormControlLabel
              control={
                <Switch
                  checked={flexIgnoreZonesEnabled}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    if (enabled) {
                      setAutoPatrolFlexIgnoreZonesEnabledForSite(siteId, true);
                      setAutoPatrolFlexIgnoreZonesWizardOpenForSite(siteId, true);
                      return;
                    }
                    setOffboardingDialogOpen(true);
                  }}
                  color="primary"
                />
              }
              label={
                <span className="font-mulish font-bold text-[14px]">
                  Flex Ignore Zones
                </span>
              }
            />
          </div>
        )}
      </div>
      {((showApProductBulkUpdate && autoPatrolOnlySchedules.length >= 2 && !editMode) ||
        (onEditSchedulesClick != null &&
          onSaveSchedulesClick != null &&
          onCancelSchedulesClick != null &&
          permissions != null)) && (
        <div className="flex shrink-0 items-center gap-2 pt-3 pr-3">
          {!editMode && showApProductBulkUpdate && autoPatrolOnlySchedules.length >= 2 && (
            <OrangeTextButton
              onClick={() => setProductBulkUpdateDialogOpen(true)}
              text="Product Bulk Update"
              disabled={false}
            />
          )}
          {!editMode ? (
            onEditSchedulesClick != null &&
            onSaveSchedulesClick != null &&
            onCancelSchedulesClick != null &&
            permissions != null &&
            (permissions.add_cameras || permissions.edit_cameras) && (
              <OrangeTextButton
                onClick={onEditSchedulesClick}
                text={
                  <>
                    <EditIcon fontSize="small" />
                    &nbsp;Edit Schedules
                  </>
                }
                disabled={false}
              />
            )
          ) : (
            onSaveSchedulesClick != null &&
            onCancelSchedulesClick != null && (
              <>
                <OrangeTextButton
                  onClick={onSaveSchedulesClick}
                  text="SAVE CHANGES"
                  disabled={false}
                />
                <OrangeTextButton
                  onClick={onCancelSchedulesClick}
                  text="CANCEL"
                  disabled={false}
                />
              </>
            )
          )}
        </div>
      )}
    </div>
  );
  const selectedScheduleRow =
    autoPatrolSchedules !== null && autoPatrolSchedules.length > 0 ? (
      <div className="flex items-center gap-3 pl-4 pt-2 pb-2">
        <p className="text-actuate-blue font-mulish font-bold text-[16px]">
          Selected Schedule:
        </p>
        <AutoPatrolScheduleDropDown />
      </div>
    ) : null;

  const noSchedules = (
    <div>
      <TableContainer
        component={Paper}
        sx={{ width: "100%", mb: 2, height: "50px" }}
      >
        {schedulesHeader}
        <div className="text-center text-gray-500 pt-3">No schedules found</div>
      </TableContainer>
    </div>
  );

  // Early return if no schedules exist
  if (!autoPatrolSchedules.length || !currentSchedule) {
    return noSchedules;
  }

  const scheduleTabAutoPatrol = (
    <div>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          mb: 2,
          "& .MuiTableHead-root .MuiTableCell-root": {
            borderBottom: "none !important",
          },
          "& .MuiTableCell-head": { borderBottom: "none !important" },
        }}
      >
        {/* <AutoPatrolScheduleTabs /> */}
        {schedulesHeader}
        {selectedScheduleRow}
        <Table
          sx={{
            width: "100%",
            fontFamily: "Mulish",
            tableLayout: "fixed",
          }}
        >
          <TableHead>
            <TableRow sx={{ height: "50px" }}>
              {titlesArray.map((title: any) => {
                return (
                  <TableCell
                    key={title}
                    align="left"
                    sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}
                  >
                    {title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody key={currentSchedule.id || currentSchedule.title}>
            <TableRow sx={{ height: "50px" }}>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {currentSchedule.scheduleStatus
                  ? currentSchedule.scheduleStatus
                  : "--"}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {currentSchedule.patrolType ? currentSchedule.patrolType : "--"}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {currentSchedule.cadence ? (
                  <div>
                    <div>
                      {`${currentSchedule.cadence} ${currentSchedule?.cadenceUnit}`}
                    </div>
                    {currentSchedule.checksPerDay && (
                      <div className="text-gray-400">
                        {`${currentSchedule.checksPerDay} checks per day`}
                      </div>
                    )}
                  </div>
                ) : currentSchedule.cronDescription ? (
                  currentSchedule.cronDescription
                ) : (
                  "--"
                )}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {currentSchedule.updatedDate
                  ? isoDateToUPCCovertor(
                      currentSchedule.updatedDate,
                      currentSchedule.olsonTimeZoneId
                    ).toString()
                  : "--"}
                &nbsp;
                {currentSchedule.updatedDate &&
                currentSchedule.olsonTimeZoneId ? (
                  <Tooltip
                    title={currentSchedule.olsonTimeZoneId}
                    placement="top"
                    sx={{ fontSize: "15px", fontFamily: "mulish" }}
                  >
                    <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                  </Tooltip>
                ) : (
                  ""
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table
          sx={{
            width: "100%",
            fontFamily: "Mulish",
            tableLayout: "fixed",
          }}
        >
          <TableHead>
            <TableRow sx={{ height: "50px" }}>
              {titlesArray2.map((title: any) => {
                return (
                  <TableCell
                    key={title}
                    align="left"
                    sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}
                  >
                    {title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {sceneChangeMetric?.display && (
                <TableCell>
                  {capitalizeWords(currentSchedule.sensitivityLevel)}
                </TableCell>
              )}
              {!isAutoPatrolSchedule && (
                <TableCell sx={{ textAlign: "left" }}>
                  <FormGroup>
                    <div className="flex">
                      {vchMetrics.map((metric: any) => (
                        <FormControlLabel
                          key={metric.id}
                          sx={{
                            marginLeft: "1px",
                            "& .MuiFormControlLabel-label": {
                              fontSize: "14px",
                              fontFamily: "Mulish",
                            },
                          }}
                          control={
                            <Checkbox
                              checked={Boolean(metric.enabled)}
                              disabled={true}
                              name={metric?.metric_type?.value}
                            />
                          }
                          label={
                            metricLabelMap[metric?.metric_type?.value] ||
                            metric?.metric_type?.name ||
                            metric?.metric_type?.value ||
                            "Metric"
                          }
                          labelPlacement="top"
                        />
                      ))}
                      {vchMetrics.length === 0 && <div>--</div>}
                    </div>
                  </FormGroup>
                </TableCell>
              )}
              {imageQualityMetric?.enabled && (
                <TableCell>
                  {imageQualityMetric?.minimum_resolution_height ? (
                    <div>{`${imageQualityMetric?.minimum_resolution_height} px`}</div>
                  ) : (
                    <div className="pl-[50px]">--</div>
                  )}
                </TableCell>
              )}
              <TableCell>
                {`${currentSchedule.scheduleCameraCount} of ${currentSchedule.siteCameraCount} cameras`}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {showProductsCheckboxesApSchedulesTab &&
          currentSchedule?.patrolType === "AutoPatrol" && (
            <Table
              sx={{
                width: "100%",
                fontFamily: "Mulish",
                tableLayout: "fixed",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      Products
                      <Tooltip
                        title="Control which products generate alerts for this schedule. Only products that are enabled at the camera level AND checked here will raise alerts. Uncheck a product here to stop alerts during this schedule."
                        placement="top"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ textAlign: "left" }}>
                    <FormGroup>
                      <div
                        className="flex flex-wrap"
                        style={{ gap: "20px", alignItems: "stretch" }}
                      >
                        {productGroups.map((group, index) => (
                          <React.Fragment key={group.parentName}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  fontWeight: 900,
                                  fontSize: "14px",
                                  fontFamily: "Mulish",
                                  marginBottom: "8px",
                                }}
                              >
                                <span>{group.parentName}</span>
                                <Checkbox
                                  data-testid={`master-select-all-${group.parentName}`}
                                  checked={areAllChildrenEnabled(group)}
                                  indeterminate={isSomeButNotAllChildrenEnabled(
                                    group
                                  )}
                                  disabled={true}
                                  size="small"
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  justifyContent: "center",
                                }}
                              >
                                {group.children.map((displayName) => (
                                  <FormControlLabel
                                    key={displayName}
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "14px",
                                        fontFamily: "Mulish",
                                      },
                                    }}
                                    control={
                                      <Checkbox
                                        checked={isProductEnabled(displayName)}
                                        disabled={true}
                                        name={displayName
                                          .toLowerCase()
                                          .replace(/\s+/g, "_")}
                                      />
                                    }
                                    label={getShortLabel(displayName)}
                                    labelPlacement="top"
                                  />
                                ))}
                              </div>
                            </div>
                            {index < productGroups.length - 1 && (
                              <div
                                style={{
                                  width: "1px",
                                  backgroundColor: "#ccc",
                                  alignSelf: "stretch",
                                  marginTop: "8px",
                                  marginBottom: "8px",
                                }}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </FormGroup>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        {showAutoPatrolFlexIgnoreZones &&
          !vCHOnlySchedules &&
          siteId > 0 &&
          flexIgnoreZonesEnabled &&
          currentSchedule?.patrolType === "AutoPatrol" && (
            <AutoPatrolFlexIgnoreZonesScheduleSection
              schedule={currentSchedule}
              editMode={false}
            />
          )}
      </TableContainer>
      {showAutoPatrolFlexIgnoreZones && siteId > 0 && (
        <AutoPatrolFlexIgnoreZonesOnboardingDialog
          siteId={siteId}
          schedules={autoPatrolOnlySchedules}
          open={flexWizardOpen}
          onClose={() => setAutoPatrolFlexIgnoreZonesWizardOpenForSite(siteId, false)}
        />
      )}
      {showAutoPatrolFlexIgnoreZones && siteId > 0 && (
        <AutoPatrolFlexIgnoreZonesOffboardingDialog
          open={offboardingDialogOpen}
          siteId={siteId}
          schedules={autoPatrolOnlySchedules}
          onClose={() => {
            setOffboardingDialogOpen(false);
            window.dispatchEvent(new CustomEvent("refreshSiteCamerasTable"));
          }}
          onSubmitted={() => {
            setAutoPatrolFlexIgnoreZonesEnabledForSite(siteId, false);
            resetAutoPatrolFlexIgnoreZonesForSite(siteId);
            window.dispatchEvent(new CustomEvent("refreshSiteCamerasTable"));
          }}
        />
      )}
      <ProductBulkUpdateDialog
        open={productBulkUpdateDialogOpen}
        schedules={autoPatrolOnlySchedules}
        autoPatrolCameraProducts={autoPatrolCameraProducts}
        onClose={() => setProductBulkUpdateDialogOpen(false)}
      />
    </div>
  );

  const scheduleTabAutoPatrolEditMode = (
    <div>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          mb: 2,
          "& .MuiTableHead-root .MuiTableCell-root": {
            borderBottom: "none !important",
          },
          "& .MuiTableCell-head": { borderBottom: "none !important" },
        }}
      >
        {/* <AutoPatrolScheduleTabs /> */}
        {schedulesHeader}
        {selectedScheduleRow}
        <Table
          sx={{
            width: "100%",
            fontFamily: "Mulish",
            tableLayout: "fixed",
          }}
        >
          <TableHead>
            <TableRow>
              {titlesArray.map((title: any) => {
                return (
                  <TableCell
                    key={title}
                    align="left"
                    sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}
                  >
                    {title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody key={currentSchedule.id || currentSchedule.title}>
            <TableRow sx={{ height: "70px" }}>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {currentSchedule.scheduleStatus
                  ? currentSchedule.scheduleStatus
                  : "--"}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {currentSchedule.patrolType ? currentSchedule.patrolType : "--"}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {currentSchedule.cadence ? (
                  <div>
                    <div>
                      {`${currentSchedule.cadence} ${currentSchedule?.cadenceUnit}`}
                    </div>
                    {currentSchedule.checksPerDay && (
                      <div className="text-gray-400">
                        {`${currentSchedule.checksPerDay} checks per day`}
                      </div>
                    )}
                  </div>
                ) : currentSchedule.cronDescription ? (
                  currentSchedule.cronDescription
                ) : (
                  "--"
                )}
              </TableCell>
              <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                {currentSchedule.updatedDate
                  ? isoDateToUPCCovertor(
                      currentSchedule.updatedDate,
                      currentSchedule.olsonTimeZoneId
                    ).toString()
                  : "--"}
                &nbsp;
                {currentSchedule.updatedDate &&
                currentSchedule.olsonTimeZoneId ? (
                  <Tooltip
                    title={currentSchedule.olsonTimeZoneId}
                    placement="top"
                    sx={{ fontSize: "15px", fontFamily: "mulish" }}
                  >
                    <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                  </Tooltip>
                ) : (
                  ""
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table
          sx={{
            width: "100%",
            fontFamily: "Mulish",
            tableLayout: "fixed",
          }}
        >
          <TableHead>
            <TableRow>
              {titlesArray2.map((title: any) => {
                return (
                  <TableCell
                    key={title}
                    align="left"
                    sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}
                  >
                    {title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {sceneChangeMetric?.display && (
                <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
                  <Autocomplete
                    disablePortal
                    options={sensitivityOptions.map((option: string) => {
                      return capitalizeWords(option);
                    })}
                    value={capitalizeWords(currentSchedule.sensitivityLevel)}
                    onChange={(event: any, newInputValue: any) => {
                      const updatedAutoPatrolSchedules = autoPatrolSchedules;
                      updatedAutoPatrolSchedules[
                        selectedAutoPatrolScheduleIndex
                      ].sensitivityLevel = lowerCaseWords(newInputValue);
                      setAutoPatrolSchedules(updatedAutoPatrolSchedules);
                      setAutoPatrolSchedulesUpdatedBoolean(true);
                      const newUpdatedAutoPatrolScheduleIndexes =
                        updatedAutoPatrolScheduleIndexes;
                      if (
                        !newUpdatedAutoPatrolScheduleIndexes.includes(
                          selectedAutoPatrolScheduleIndex.toString()
                        )
                      ) {
                        newUpdatedAutoPatrolScheduleIndexes.push(
                          selectedAutoPatrolScheduleIndex.toString()
                        );
                        setUpdatedAutoPatrolScheduleIndexes(
                          newUpdatedAutoPatrolScheduleIndexes
                        );
                      }
                    }}
                    sx={{ width: 150 }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Sensitivity"
                      />
                    )}
                  />
                </TableCell>
              )}
              {!isAutoPatrolSchedule && (
                <TableCell sx={{ textAlign: "left" }}>
                  <FormGroup>
                    <div className="flex">
                      {vchMetrics.map((metric: any) => (
                        <FormControlLabel
                          key={metric.id}
                          sx={{
                            marginLeft: "1px",
                            "& .MuiFormControlLabel-label": {
                              fontSize: "14px",
                              fontFamily: "Mulish",
                            },
                          }}
                          control={
                            <Checkbox
                              checked={Boolean(metric.enabled)}
                              onChange={(event: any) =>
                                updateMetricEnabled(
                                  metric?.metric_type?.value,
                                  event.target.checked
                                )
                              }
                            />
                          }
                          labelPlacement="top"
                          label={
                            metricLabelMap[metric?.metric_type?.value] ||
                            metric?.metric_type?.name ||
                            metric?.metric_type?.value ||
                            "Metric"
                          }
                        />
                      ))}
                    </div>
                  </FormGroup>
                </TableCell>
              )}
              {imageQualityMetric?.enabled && (
                <TableCell>
                  <TextField
                    value={imageQualityMetric?.minimum_resolution_height}
                    onChange={(event: any) => {
                      const updatedScheduleMetrics = scheduleMetrics.map(
                        (metric: any) => {
                          if (
                            metric?.metric_type?.value === "image_quality_check"
                          ) {
                            return {
                              ...metric,
                              minimum_resolution_height: event.target.value,
                            };
                          }
                          return metric;
                        }
                      );
                      setAutoPatrolScheduleHealthchckMetrics({
                        ...autoPatrolScheduleHealthchckMetrics,
                        [scheduleId]: updatedScheduleMetrics,
                      });
                      markScheduleHealthMetricsUpdated();
                    }}
                    error={
                      imageQualityMetric?.minimum_resolution_height < 360
                    }
                    helperText={
                      imageQualityMetric?.minimum_resolution_height < 360
                        ? "Any resolution below 360 px may experience poor scene change performance."
                        : ""
                    }
                    variant="standard"
                    sx={{ width: 150 }}
                    type="number"
                    inputProps={{ min: 0 }}
                    InputLabelProps={{ shrink: true }}
                    autoComplete="off"
                  />
                </TableCell>
              )}
              <TableCell>
                {`${currentSchedule.scheduleCameraCount} of ${currentSchedule.siteCameraCount} cameras`}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {showProductsCheckboxesApSchedulesTab &&
          currentSchedule?.patrolType === "AutoPatrol" && (
            <Table
              sx={{
                width: "100%",
                fontFamily: "Mulish",
                tableLayout: "fixed",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      Products
                      <Tooltip
                        title="Control which products generate alerts for this schedule. Only products that are enabled at the camera level AND checked here will raise alerts. Uncheck a product here to stop alerts during this schedule."
                        placement="top"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ textAlign: "left" }}>
                    <FormGroup>
                      <div
                        className="flex flex-wrap"
                        style={{ gap: "20px", alignItems: "stretch" }}
                      >
                        {productGroups.map((group, index) => (
                          <React.Fragment key={group.parentName}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  fontWeight: 900,
                                  fontSize: "14px",
                                  fontFamily: "Mulish",
                                  marginBottom: "8px",
                                  color: group.color,
                                }}
                              >
                                <span>{group.parentName}</span>
                                <Checkbox
                                  data-testid={`master-select-all-${group.parentName}`}
                                  checked={areAllChildrenEnabled(group)}
                                  indeterminate={isSomeButNotAllChildrenEnabled(
                                    group
                                  )}
                                  disabled={false}
                                  size="small"
                                  onChange={() =>
                                    handleMasterCheckboxChange(group)
                                  }
                                  sx={{
                                    color: group.color,
                                    "&.Mui-checked": {
                                      color: group.color,
                                    },
                                    "&.MuiCheckbox-indeterminate": {
                                      color: group.color,
                                    },
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  justifyContent: "center",
                                }}
                              >
                                {group.children.map((displayName) => (
                                  <FormControlLabel
                                    key={displayName}
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "14px",
                                        fontFamily: "Mulish",
                                      },
                                    }}
                                    control={
                                      <Checkbox
                                        checked={isProductEnabled(displayName)}
                                        disabled={false}
                                        onChange={() =>
                                          handleProductCheckboxChange(
                                            displayName
                                          )
                                        }
                                        name={displayName
                                          .toLowerCase()
                                          .replace(/\s+/g, "_")}
                                        sx={{
                                          color: group.color,
                                          "&.Mui-checked": {
                                            color: group.color,
                                          },
                                        }}
                                      />
                                    }
                                    label={getShortLabel(displayName)}
                                    labelPlacement="top"
                                  />
                                ))}
                              </div>
                            </div>
                            {index < productGroups.length - 1 && (
                              <div
                                style={{
                                  width: "1px",
                                  backgroundColor: "#ccc",
                                  alignSelf: "stretch",
                                  marginTop: "8px",
                                  marginBottom: "8px",
                                }}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </FormGroup>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        {showAutoPatrolFlexIgnoreZones &&
          !vCHOnlySchedules &&
          siteId > 0 &&
          flexIgnoreZonesEnabled &&
          currentSchedule?.patrolType === "AutoPatrol" && (
            <AutoPatrolFlexIgnoreZonesScheduleSection
              schedule={currentSchedule}
              editMode={true}
            />
          )}
      </TableContainer>
      {showAutoPatrolFlexIgnoreZones && siteId > 0 && (
        <AutoPatrolFlexIgnoreZonesOnboardingDialog
          siteId={siteId}
          schedules={autoPatrolOnlySchedules}
          open={flexWizardOpen}
          onClose={() => setAutoPatrolFlexIgnoreZonesWizardOpenForSite(siteId, false)}
        />
      )}
      {showAutoPatrolFlexIgnoreZones && siteId > 0 && (
        <AutoPatrolFlexIgnoreZonesOffboardingDialog
          open={offboardingDialogOpen}
          siteId={siteId}
          schedules={autoPatrolOnlySchedules}
          onClose={() => {
            setOffboardingDialogOpen(false);
            window.dispatchEvent(new CustomEvent("refreshSiteCamerasTable"));
          }}
          onSubmitted={() => {
            setAutoPatrolFlexIgnoreZonesEnabledForSite(siteId, false);
            resetAutoPatrolFlexIgnoreZonesForSite(siteId);
            window.dispatchEvent(new CustomEvent("refreshSiteCamerasTable"));
          }}
        />
      )}
      <ProductBulkUpdateDialog
        open={productBulkUpdateDialogOpen}
        schedules={autoPatrolOnlySchedules}
        autoPatrolCameraProducts={autoPatrolCameraProducts}
        onClose={() => setProductBulkUpdateDialogOpen(false)}
      />
    </div>
  );

  return autoPatrolSchedules.length > 0
    ? editMode
      ? scheduleTabAutoPatrolEditMode
      : scheduleTabAutoPatrol
    : noSchedules;
};
